import React, { FC } from 'react';
import styled from 'styled-components';
import { Filter } from 'components/molecules/filters/types';
import { FieldType } from 'components/organisms/tasks/fields';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { AssigneeFieldFilter } from 'components/organisms/tasks/filters/assignee';
import { BooleanFieldFilter } from 'components/organisms/tasks/filters/boolean';
import { DateFieldFilter } from 'components/organisms/tasks/filters/date';
import { EstimateFieldFilter } from 'components/organisms/tasks/filters/estimate';
import { MultiSelectFieldFilter } from 'components/organisms/tasks/filters/multi-select';
import { MultiUserFieldFilter } from 'components/organisms/tasks/filters/multi-user';
import { NumberFieldFilter } from 'components/organisms/tasks/filters/number';
import { SingleSelectFieldFilter } from 'components/organisms/tasks/filters/single-select';
import { SingleUserFieldFilter } from 'components/organisms/tasks/filters/single-user';
import { TextFieldFilter } from 'components/organisms/tasks/filters/text';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { useTranslate } from 'hooks/use-translation';
import { unreachable } from 'utils/unreachable';
import { AssigneeFilterToText } from './assignee-filter';
import { BooleanFilterToText } from './boolean-filter';
import { DateFilterToText } from './date-filter';
import { EstimateFilterToText } from './estimate-filter';
import { MultiSelectFilterToText } from './multi-select-filter';
import { MultiUserFilterToText } from './multi-user-filter';
import { NumberFilterToText } from './number-filter';
import { ResponsibleFilterToText } from './responsible-filter';
import { SingleSelectFilterToText } from './single-select-filter';
import { SingleUserFilterToText } from './single-user-filter';
import { TextFilterToText } from './text-filter';

const CriteriaList = styled.ul`
  padding-left: var(--spacing-06);
`;
const CriteriaHeading = styled.div`
  background-color: var(--gray-70);
  padding: var(--spacing-02) var(--spacing-03);
  width: 100%;
`;

export const FilterCriteriaAsText: FC<{ criterion: FieldFilter }> = ({
  criterion,
}) => {
  const {
    field: { type },
  } = criterion;

  switch (type) {
    case FieldType.Boolean:
      return <BooleanFilterToText filter={criterion as BooleanFieldFilter} />;
    case FieldType.Number:
      return <NumberFilterToText filter={criterion as NumberFieldFilter} />;
    case FieldType.Text:
    case FieldType.MultiLineText:
      return <TextFilterToText filter={criterion as TextFieldFilter} />;
    case FieldType.Date:
      return <DateFilterToText filter={criterion as DateFieldFilter} />;
    case FieldType.SingleSelect:
      return (
        <SingleSelectFilterToText
          filter={criterion as SingleSelectFieldFilter}
        />
      );
    case FieldType.SingleUser:
      return (
        <SingleUserFilterToText filter={criterion as SingleUserFieldFilter} />
      );
    case FieldType.MultiUser:
      return (
        <MultiUserFilterToText filter={criterion as MultiUserFieldFilter} />
      );
    case FieldType.MultiSelect:
      return criterion.field.fieldName === 'responsible' ? (
        <ResponsibleFilterToText filter={criterion as MultiSelectFieldFilter} />
      ) : (
        <MultiSelectFilterToText filter={criterion as MultiSelectFieldFilter} />
      );
    case FieldType.Assignee:
      return <AssigneeFilterToText filter={criterion as AssigneeFieldFilter} />;

    case FieldType.Estimate:
      return <EstimateFilterToText filter={criterion as EstimateFieldFilter} />;
    default:
      return unreachable(type);
  }
};

export const FilterAsText: FC<{
  filter: GroupedFilter;
  dashboardFilter?: Filter;
}> = ({ filter: { groupCriteria, type }, dashboardFilter }) => {
  const translate = useTranslate();

  return (
    <>
      {groupCriteria.length ? (
        <>
          <CriteriaHeading>
            {translate('dashboard.widget.filters.combiner_type_text', {
              combinerType: type,
              widgetType: 'chart',
            })}
          </CriteriaHeading>
          <CriteriaList>
            {groupCriteria.map(({ criteria }) => (
              <li>
                <FilterCriteriaAsText criterion={criteria} />
              </li>
            ))}
          </CriteriaList>
        </>
      ) : null}

      {dashboardFilter && dashboardFilter.criteria?.length ? (
        <>
          <CriteriaHeading>
            {translate('dashboard_global.filters.combiner_type_text', {
              combinerType: dashboardFilter.type,
            })}
          </CriteriaHeading>
          <CriteriaList>
            {dashboardFilter.criteria.map((criteria) => (
              <li>
                <FilterCriteriaAsText criterion={criteria} />
              </li>
            ))}
          </CriteriaList>
        </>
      ) : null}
    </>
  );
};
